import { getHttpClient } from '@/services/httpClientProvider';
import { useAuthStore } from '@/stores/auth';

export interface View {
    id: string;
    label: string;
    type?: number;
    status?: number;
    object?: string;
    sort_object?: string;
    sort_dir?: string;
    group_by?: string;
    team_ids?: any[];
    layout?: string;
    creator_users_id?: string;
    filters?: Array<{
        filter_field: string;
        filter_operator: string;
        filter_field_lbl: string;
        filter_operator_lbl: string;
        filter_compare_field: any;
        filter_compare_field_lbl: string | string[];
        filter_compare_field2_lbl?: string;
    }>;
    _uiAccess?: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
}

export interface IRole {
    id: string;
    partners_id: string;
    label: string;
    start_page: string;
    perms: string[];
    created: string;
    updated: string;
    permSet: Record<string, string>;
    package_id: string;
    packagePerms: string[];
    packagePermSet: Record<string, string>;
    metadata: any;
    c__perms: string;
    c__org_name: string;
    c__package: string;
    c__user_count: number;
    c__perm_count: number;
    c__users: string[];
    _highlighted: boolean;
    _highlightmap: Record<string, any>;
    _canWrite: boolean;
    _uiAccess: {
        edit: boolean;
        delete: boolean;
        clone: boolean;
        merge: boolean;
    };
    _has_detail: boolean;
    // All the p__ fields for individual permissions
    [key: `p__${string}`]: boolean;
}

export interface IRolePermissionGroup {
    title: string;
    description: string;
    perms: Array<{
        label: string;
        description: string | null;
        perm: string;
        field: string;
        limit: any;
    }>;
}

export interface SettingsResponse {
    success: boolean;
    message?: string;
    relay_views?: {
        totalCount?: number;
        results: View[];
    };
    current_server_time?: string;
}

export interface RolesListResponse {
    success: boolean;
    partners_perms_roles: {
        totalCount: number;
        results: IRole[];
    };
    role_meta: {
        defs: IRolePermissionGroup[];
    };
    current_server_time: string;
}

/**
 * Composable that provides access to Settings API functions.
 * This safely initializes the httpClient with authStore only when called 
 * inside components or other composables after Pinia is ready.
 */
export function useSettingsAPI() {
    const authStore = useAuthStore();
    const httpClient = getHttpClient(authStore);

    return {
        async fetchViews(): Promise<View[]> {
            try {            
                const data = await httpClient.get<SettingsResponse>('admin/v4/settings/', {
                    sAction: 'viewsListing',
                    page: '1',
                    start: '0',
                    limit: '50',
                });

                if (data.success && data.relay_views?.results) {
                    return data.relay_views.results;
                }
                
                console.error('❌ SettingsAPI: No views data in response', data);
                throw new Error(data.message || 'Failed to fetch views');
            } catch (error) {
                console.error('❌ SettingsAPI: Error fetching views:', error);
                throw error;
            }
        },

        async fetchRoles(params: {
            page?: number;
            start?: number;
            limit?: number;
            sort?: Array<{ property: string; direction: string }>;
            filter?: Array<{ property: string; value: string }>;
        } = {}): Promise<RolesListResponse> {
            try {
                const {
                    page = 1,
                    start = 0,
                    limit = 25,
                    sort = [{ property: 'updated', direction: 'DESC' }],
                    filter = [
                        { property: 'id', value: '_no_filter_' },
                        { property: 'partners_id', value: '' } // Will be filled by backend
                    ]
                } = params;

                const data = await httpClient.get<RolesListResponse>('admin/v4/settings/', {
                    sAction: 'rolesListing',
                    page: page.toString(),
                    start: start.toString(),
                    limit: limit.toString(),
                    sort: JSON.stringify(sort),
                    filter: JSON.stringify(filter),
                });

                if (data.success && data.partners_perms_roles) {
                    return data;
                }
                
                console.error('❌ SettingsAPI: No roles data in response', data);
                throw new Error('Failed to fetch roles');
            } catch (error) {
                console.error('❌ SettingsAPI: Error fetching roles:', error);
                throw error;
            }
        },

        async fetchRoleById(roleId: string, partnerId?: string): Promise<IRole> {
            try {
                // Get partner ID from user store if not provided
                let partnerIdToUse = partnerId;
                if (!partnerIdToUse) {
                    const { useUserStore } = await import('@/stores/user');
                    const userStore = useUserStore();
                    partnerIdToUse = userStore.userData?.object_id;
                }

                const filter: Array<{ property: string; value: string }> = [
                    { property: 'id', value: roleId },
                ];

                // Add partners_id filter if available
                if (partnerIdToUse) {
                    filter.push({ property: 'partners_id', value: partnerIdToUse });
                }

                const data = await httpClient.get<RolesListResponse>('admin/v4/settings/', {
                    sAction: 'rolesListing',
                    page: '1',
                    start: '0',
                    limit: '1',
                    sort: JSON.stringify([{ property: 'id', direction: 'ASC' }]),
                    filter: JSON.stringify(filter),
                });

                if (data.success && data.partners_perms_roles?.results?.length > 0) {
                    return data.partners_perms_roles.results[0];
                }

                console.error('❌ SettingsAPI: No role found with ID:', roleId);
                throw new Error('Role not found');
            } catch (error) {
                console.error('❌ SettingsAPI: Error fetching role:', error);
                throw error;
            }
        },

        async createRole(roleData: {
            label: string;
            start_page?: string;
            permissions: Record<string, boolean>;
        }): Promise<IRole> {
            try {
                // Get org ID from authenticated user
                const { useUserStore } = await import('@/stores/user');
                const userStore = useUserStore();
                const orgId = userStore.userData?.context_org_id || userStore.userData?.object_id;
                
                if (!orgId) {
                    throw new Error('No organization ID available - user context not loaded');
                }
                
                const payload = {
                    partners_id: orgId,
                    id: 'settings.PartnersPermsRolesModel-3',
                    label: roleData.label,
                    start_page: roleData.start_page || '',
                    updated: '',
                    created: '',
                    c__user_count: 0,
                    _user_list: '-',
                };

                const formData = new URLSearchParams();
                formData.append('partners_perms_roles', JSON.stringify([payload]));
                
                const data = await httpClient.post<RolesListResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'rolesPut',
                    }
                );

                if (data.success && data.partners_perms_roles?.results?.length > 0) {
                    return data.partners_perms_roles.results[0];
                }

                console.error('❌ SettingsAPI: Failed to create role', data);
                throw new Error('Failed to create role');
            } catch (error) {
                console.error('❌ SettingsAPI: Error creating role:', error);
                throw error;
            }
        },

        async updateRole(roleData: {
            id: string;
            partners_id: string;
            label: string;
            start_page?: string;
            permissions: Record<string, boolean>;
        }): Promise<IRole> {
            try {
                // Build the p__ fields from permissions
                const permFields: Record<string, boolean> = {};
                Object.entries(roleData.permissions).forEach(([key, value]) => {
                    permFields[`p__${key}`] = value;
                });

                const payload = {
                    partners_id: roleData.partners_id,
                    id: roleData.id,
                    label: roleData.label,
                    start_page: roleData.start_page || '',
                    updated: '',
                    created: '',
                    c__user_count: 0,
                    _user_list: '-',
                    ...permFields,
                };

                const formData = new URLSearchParams();
                formData.append('partners_perms_roles', JSON.stringify([payload]));
                
                const data = await httpClient.post<RolesListResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'rolesPut',
                    }
                );

                if (data.success && data.partners_perms_roles?.results?.length > 0) {
                    return data.partners_perms_roles.results[0];
                }

                console.error('❌ SettingsAPI: Failed to update role', data);
                throw new Error('Failed to update role');
            } catch (error) {
                console.error('❌ SettingsAPI: Error updating role:', error);
                throw error;
            }
        },

        async cloneRole(role: IRole): Promise<RolesListResponse> {
            try {
                // Extract permission IDs from the role's perms array
                const perms = role.perms || [];
                
                // Build permSet from p__ fields that are true
                const permSet: Record<string, string> = {};
                Object.keys(role).forEach(key => {
                    if (key.startsWith('p__') && (role as any)[key] === true) {
                        const permName = key.substring(3);
                        permSet[permName] = permName;
                    }
                });

                // Build the payload with all the role details
                const payload: any = {
                    partners_id: role.partners_id,
                    label: role.label,
                    start_page: role.start_page || '',
                    perms,
                    created: '',
                    updated: role.updated,
                    permSet,
                    package_id: role.package_id,
                    packagePerms: role.packagePerms,
                    packagePermSet: role.packagePermSet,
                    metadata: role.metadata,
                    c__perms: role.c__perms,
                    c__org_name: role.c__org_name,
                    c__package: role.c__package,
                    c__user_count: role.c__user_count,
                    c__perm_count: role.c__perm_count,
                    c__users: role.c__users || [],
                    _highlighted: role._highlighted || false,
                    _highlightmap: role._highlightmap || {},
                    _canWrite: role._canWrite,
                    _uiAccess: role._uiAccess,
                    _has_detail: role._has_detail,
                    _user_list: '-',
                    id: role.id,
                    _source: role.id,
                    _clone: true,
                    email: '09c24475ecbbea6775a82df3b48792e7@goboomtown.com',
                    phone: '',
                    sms: '',
                    nickname: '',
                    viewer_data: '',
                };

                // Add all p__ permission fields
                Object.keys(role).forEach(key => {
                    if (key.startsWith('p__')) {
                        payload[key] = (role as any)[key];
                    }
                });

                const formData = new URLSearchParams();
                formData.append('partners_perms_roles', JSON.stringify([payload]));
                
                const data = await httpClient.post<RolesListResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'rolesPut',
                    }
                );

                if (data.success && data.partners_perms_roles?.results?.length > 0) {
                    return data;
                }

                console.error('❌ SettingsAPI: Failed to clone role', data);
                throw new Error('Failed to clone role');
            } catch (error) {
                console.error('❌ SettingsAPI: Error cloning role:', error);
                throw error;
            }
        },

        async deleteRole(roleId: string): Promise<void> {
            try {
                const payload = {
                    id: roleId,
                };

                const formData = new URLSearchParams();
                formData.append('partners_perms_roles', JSON.stringify([payload]));
                
                const data = await httpClient.post<{ success: boolean; current_server_time: string }>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'rolesDelete',
                    }
                );

                if (!data.success) {
                    console.error('❌ SettingsAPI: Failed to delete role', data);
                    throw new Error('Failed to delete role');
                }
            } catch (error) {
                console.error('❌ SettingsAPI: Error deleting role:', error);
                throw error;
            }
        },

        async createView(viewData: {
            label: string;
            type: number;
            sortObject: string;
            teamIds?: string[];
            shareWithAllTeams?: boolean;
            filters?: any[];
        }): Promise<View> {
            try {
                // Build the payload for the new view
                const payload: any = {
                    type: viewData.type,
                    layout: 'card',
                    partners_id: '', // Will be set by backend
                    object: 'issues',
                    sort_object: viewData.sortObject,
                    filters: viewData.filters || [],
                    group_by: '',
                    id: `boomtown.model.settings.RelayViewsModel-${Date.now()}`, // Temporary ID
                    owner_org_id: '',
                    creator_users_id: '',
                    label: viewData.label,
                    status: 0,
                    sort_dir: '',
                    updated: '',
                    created: ''
                };

                // Handle team_ids for shared views
                if (viewData.type === 3) {
                    if (viewData.shareWithAllTeams) {
                        // Share with all teams - team_ids should be empty string
                        payload.team_ids = '';
                    } else if (viewData.teamIds && viewData.teamIds.length > 0) {
                        // Share with specific teams
                        payload.team_ids = viewData.teamIds;
                    } else {
                        // Shared view but no teams selected - default to empty string
                        payload.team_ids = '';
                    }
                }

                const formData = new URLSearchParams();
                formData.append('relay_views', JSON.stringify([payload]));
                
                const data = await httpClient.post<SettingsResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'viewsPut',
                    }
                );

                if (data.success && data.relay_views?.results && data.relay_views.results.length > 0) {
                    return data.relay_views.results[0];
                }

                console.error('❌ SettingsAPI: Failed to create view', data);
                throw new Error('Failed to create view');
            } catch (error) {
                console.error('❌ SettingsAPI: Error creating view:', error);
                throw error;
            }
        },

        async updateView(viewData: {
            id: string;
            label: string;
            type: number;
            sortObject: string;
            teamIds?: string[];
            shareWithAllTeams?: boolean;
            filters?: any[];
        }): Promise<View> {
            try {
                // Build a minimal payload for updating the view
                // Only send the fields that can be changed
                const payload: any = {
                    id: viewData.id,
                    label: viewData.label,
                    filters: viewData.filters || [],
                    sort_object: viewData.sortObject,
                    type: viewData.type
                };

                // Handle team_ids based on view type
                if (viewData.type === 3) {
                    // Shared view
                    if (viewData.shareWithAllTeams) {
                        // Share with all teams - team_ids should be empty string
                        payload.team_ids = '';
                    } else if (viewData.teamIds && viewData.teamIds.length > 0) {
                        // Share with specific teams
                        payload.team_ids = viewData.teamIds;
                    } else {
                        // Shared view but no teams selected - default to empty string
                        payload.team_ids = '';
                    }
                }
                // Note: For private views (type 2), we don't include team_ids at all

                const formData = new URLSearchParams();
                formData.append('relay_views', JSON.stringify([payload]));
                
                const data = await httpClient.post<SettingsResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'viewsPut',
                    }
                );

                if (data.success && data.relay_views?.results && data.relay_views.results.length > 0) {
                    return data.relay_views.results[0];
                }

                console.error('❌ SettingsAPI: Failed to update view', data);
                throw new Error('Failed to update view');
            } catch (error) {
                console.error('❌ SettingsAPI: Error updating view:', error);
                throw error;
            }
        },

        async deleteView(viewId: string): Promise<boolean> {
            try {
                const payload = {
                    id: viewId
                };

                const formData = new URLSearchParams();
                formData.append('relay_views', JSON.stringify([payload]));
                
                const data = await httpClient.post<SettingsResponse>(
                    'admin/v4/settings/', 
                    formData,
                    {
                        sAction: 'viewsDelete',
                    }
                );

                if (data.success) {
                    console.log('✅ SettingsAPI: View deleted successfully');
                    return true;
                }

                console.error('❌ SettingsAPI: Failed to delete view', data);
                throw new Error('Failed to delete view');
            } catch (error) {
                console.error('❌ SettingsAPI: Error deleting view:', error);
                throw error;
            }
        }
    };
} 